import { omit } from 'lodash';
import { UploadEnv, Uploader, type UploaderOptions, type UploadTokenRequestParams } from '@meitu/upload';
import request from '@/utils/request';
import { getApiHost } from '../utils/env';
import { getSliceConfigAsync, getUploadConfig } from './config';
import type { IObsOptions } from './types';
import { getFileType } from './utils/file';
import { statUpload } from './utils/stat';

let uploaderInstance: Uploader | null = null;

async function requestSignUploadParams(params: UploadTokenRequestParams): Promise<UploadTokenRequestParams> {
    const result = await request.get(`${getApiHost()}/maat/getsign`, {
        params: omit(params, ['sig', 'sigVersion', 'sigTime']),
    });

    const {
        sig = '',
        sig_time: sigTime = '',
        sig_version: sigVersion = '',
    } = result as unknown as {
        sig: string;
        sig_time: string;
        sig_version: string;
    };

    return {
        ...params,
        sig,
        sigTime,
        sigVersion,
    };
}

// 获取上传实例
export const getUploaderInstance = (options: Partial<UploaderOptions> = {}) => {
    if (uploaderInstance) return uploaderInstance;

    const { debug = false, env, signature, accessToken } = getUploadConfig();

    class XDUploader extends Uploader {
        async request<T = Response, R = RequestInit>(url: string, options?: Partial<R>): Promise<T> {
            url = url.indexOf('?') > -1 ? `${url}&t=1` : `${url}?t=1`;
            const res = await super.request(url, options);
            return res as T;
        }
    }

    uploaderInstance = new XDUploader({
        ...options,
        debug: options?.debug ?? debug,
        accessToken: options?.accessToken ?? accessToken,
        sign: options?.sign ?? (signature ? requestSignUploadParams : undefined),
        env: UploadEnv[env],
    } as UploaderOptions);

    return uploaderInstance;
};

// 重置上传实例
export const resetUploaderInstance = () => {
    uploaderInstance = null;
};

/**
 * @param fileData 文件
 * @returns
 */
export const uploadFileToObs = async (
    fileData: string | File,
    options?: IObsOptions,
): Promise<{ url: string; previewUrl?: string }> => {
    const {
        type,
        app,
        partSize: partSizeConfig,
        maxConcurrent: maxConcurrentConfig,
        needStat,
        asyncSliceConfig,
    } = getUploadConfig();
    const {
        debug,
        accessToken,
        signature,
        customMaatType,
        customSuffix,
        maxConcurrent: maxConcurrentOption,
        partSize: partSizeOption,
        // 上传进度
        onUploadProgress,
        maxRetries = 3,
    } = options || {};
    const maatType = customMaatType || type;

    /* 分片配置 */
    let partSize = partSizeOption || partSizeConfig;
    let maxConcurrent = maxConcurrentOption || maxConcurrentConfig;

    /**
     * 异步获取分片数据
     * 优先级：入参 > 异步获取 > 全局配置 > 默认值
     */
    if (asyncSliceConfig && (!partSizeOption || !maxConcurrentOption)) {
        const sliceConfig = await getSliceConfigAsync();
        if (!partSizeOption && sliceConfig.partSize) partSize = sliceConfig.partSize;
        if (!maxConcurrentOption && sliceConfig.maxConcurrent) maxConcurrent = sliceConfig.maxConcurrent;
    }

    /* 文件后缀 */
    let suffix = customSuffix || 'jpeg';
    if (!customSuffix) {
        if (typeof fileData === 'string') {
            // 提取 Base64 前缀中的 MIME 类型
            const match = fileData.match(/^data:(.+?);base64,/);
            const fileType = match ? match[1] : 'image/jpeg';
            suffix = fileType === 'image/svg+xml' ? 'svg' : fileType.split('/')[1];
        } else {
            const fileType = fileData.type || (await getFileType(fileData));
            suffix = fileType === 'image/svg+xml' ? 'svg' : fileType?.split('/')[1];
        }
    }

    const uploader = getUploaderInstance({
        debug,
        accessToken,
        sign: signature ? requestSignUploadParams : undefined,
    });

    const ops = {
        app,
        count: 1,
        suffix,
        type: maatType,
    } as any;

    const startTime = performance.now();

    let lastError: unknown;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            let data = await uploader.upload(fileData, ops, {
                partSize,
                maxConcurrent,
                progress: onUploadProgress,
            });
            const url = data.previewUrl || data.url;

            // 埋点
            if (needStat)
                statUpload(fileData, maatType, {
                    startTime,
                    success: true,
                    partSize,
                    maxConcurrent,
                });
            return { url, previewUrl: data.previewUrl };
        } catch (e) {
            lastError = e;
            // 如果还有重试机会，继续尝试
            if (attempt < maxRetries) {
                continue;
            }
            // 最后一次尝试失败，记录埋点并抛出错误
            if (needStat)
                statUpload(fileData, maatType, {
                    startTime,
                    success: false,
                    partSize,
                    maxConcurrent,
                });
            throw e;
        }
    }

    // 理论上不会执行到这里，但 TypeScript 需要返回值
    throw lastError;
};

/**
 * 上传base64到obs
 * @param base64
 * @returns
 */
export const uploadBase64ToObs = async (base64: string, options?: IObsOptions) => {
    return uploadFileToObs(base64, options);
};

/**
 * 根据obs地址获取宽高信息
 * @param url obs地址
 * @returns
 */
export function fetchObsURLWH(url: string): Promise<{ width: number; height: number }> {
    const reqInfo = new Request(url.includes('?') ? url + '&imageWH' : url + '?imageWH');

    return new Promise((resolve, reject) => {
        fetch(reqInfo, {
            method: 'GET',
            headers: new Headers(),
            mode: 'cors',
            cache: 'default',
        })
            .then((res) => {
                if (res.status < 200 || res.status >= 400) {
                    throw new Error('get error');
                }
                return res.json();
            })
            .then((res) => {
                resolve(res);
            })
            .catch((e) => {
                reject(e);
            });
    });
}
